import { api } from './client'

/** The account's current subscription, from `GET /api/billing/subscription`. */
export interface Subscription {
  planName: string
  status: 'Trialing' | 'Active' | 'PastDue' | 'Canceled'
  billingInterval: string
  monthlyAmountUsd: number
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  /** Set when a downgrade is scheduled for the end of the current period. */
  pendingPlanName: string | null
  /** False for an account that has never paid, however its plan reads. */
  isPaid: boolean
}

/** What a plan change did, so the UI can say so without guessing. */
export interface PlanChangeResult {
  /** True for an upgrade (applied now); false for a downgrade (scheduled). */
  effectiveNow: boolean
  planName: string
  effectiveOn: string
  creditsGranted: number
  /** Ready-to-show sentence composed server-side, where the rules live. */
  message: string
}

export const billingApi = {
  subscription(): Promise<Subscription> {
    return api.get<Subscription>('/billing/subscription')
  },

  /** Hosted checkout for an account with no paid subscription yet. */
  checkout(planId: string, interval: 'monthly' | 'yearly' = 'monthly'): Promise<{ checkoutUrl: string }> {
    return api.post<{ checkoutUrl: string }>('/billing/checkout', { planId, interval })
  },

  /**
   * Moves between plans. Upgrades apply immediately and are prorated;
   * downgrades are scheduled for period end. The result says which happened.
   */
  changePlan(planId: string): Promise<PlanChangeResult> {
    return api.post<PlanChangeResult>('/billing/plan', { planId })
  },

  /** Cancels at period end — access continues to the date already paid for. */
  cancel(): Promise<void> {
    return api.post<void>('/billing/cancel')
  },
}
